import React, { useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme
} from '@mui/material';
import { Search, Close, CheckCircle, Refresh, Send, Info } from '@mui/icons-material';
import { getRequest } from '../../ApiFunction';
import API from '../../Api';
import { toast } from 'react-toastify';
import { MESSAGE_CATEGORIES } from '../../services/messages/messagePayloadTemplates';

const LoanLookup = ({ onLoanSelected, selectedLoan, onMessageTrigger, compact = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  // Theme-aware colors
  const colors = {
    bgPrimary: isDark ? '#0F172A' : '#ffffff',
    bgSecondary: isDark ? '#1E293B' : '#f8fafc',
    border: isDark ? '#334155' : '#e2e8f0',
    textPrimary: isDark ? '#E2E8F0' : '#1e293b',
    textSecondary: isDark ? '#94A3B8' : '#64748b',
    textMuted: isDark ? '#64748B' : '#94a3b8',
    inputBg: isDark ? '#0F172A' : '#ffffff',
  };

  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [loanData, setLoanData] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const [showRecent, setShowRecent] = useState(false);
  const [recentSearches, setRecentSearches] = useState(() => {
    const saved = localStorage.getItem('essRecentSearches');
    return saved ? JSON.parse(saved) : [];
  });

  const handleSearch = async () => {
    if (!searchValue.trim()) {
      toast.error('Please enter an Application Number');
      return;
    }

    setLoading(true);
    try {
      // Search in the existing loans list
      const response = await getRequest(`${API.ALL_EMPLOYEES_LOAN}`);
      const { success, data, message } = response.data;

      if (!success) {
        toast.error(message || 'Failed to fetch loans');
        setLoanData(null);
        return;
      }

      const loans = data?.loans || [];
      
      // Case-insensitive search
      const searchTerm = searchValue.trim();
      const foundLoan = loans.find(
        loan => 
          loan.essApplicationNumber === searchTerm ||
          loan.essApplicationNumber?.toLowerCase() === searchTerm.toLowerCase() ||
          loan._id === searchTerm ||
          loan.Id?.toString() === searchTerm
      );

      if (foundLoan) {
        // Fetch loan status to get essLoanNumberAlias (LoanNumber from LoanMapping collection)
        let enrichedLoan = { ...foundLoan };
        try {
          // Use /api/frontend/loan/status/:essApplicationNumber endpoint
          const statusResponse = await getRequest(`${API.LOAN_STATUS}/${foundLoan.essApplicationNumber}`);
          if (statusResponse.data?.success && statusResponse.data?.data) {
            const loanStatus = statusResponse.data.data;
            // Add the essLoanNumberAlias from the loan status response
            if (loanStatus.loanNumber) {
              enrichedLoan.essLoanNumberAlias = loanStatus.loanNumber;
            }
            // Also get CheckNumber if available
            if (loanStatus.checkNumber) {
              enrichedLoan.essCheckNumber = loanStatus.checkNumber;
            }
            // Merge any other useful fields from the status response
            if (loanStatus.status) {
              enrichedLoan.essStatus = loanStatus.status;
            }
          }
        } catch (statusError) {
          // Loan status not available - will use fallback
        }
        
        setLoanData(enrichedLoan);
        toast.success('Loan details retrieved successfully');
        
        // In compact mode, automatically select the loan
        if (compact) {
          onLoanSelected(enrichedLoan);
        }
        
        // Add to recent searches
        const newRecentSearches = [
          searchTerm,
          ...recentSearches.filter(s => s !== searchTerm)
        ].slice(0, 5); // Keep last 5
        setRecentSearches(newRecentSearches);
        localStorage.setItem('essRecentSearches', JSON.stringify(newRecentSearches));
      } else {
        toast.error(`No loan found with identifier: ${searchValue}`);
        setLoanData(null);
      }
    } catch (error) {
      console.error('Error fetching loan:', error);
      toast.error('Error fetching loan details');
      setLoanData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleUseLoan = () => {
    if (loanData) {
      onLoanSelected(loanData);
      toast.success('Loan data loaded - ready to trigger messages');
    }
  };

  const handleClear = () => {
    setLoanData(null);
    setSearchValue('');
    onLoanSelected(null);
  };

  const handleSendMessageClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Also select the loan if not already selected
    if (loanData && (!selectedLoan || selectedLoan._id !== loanData._id)) {
      onLoanSelected(loanData);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMessageSelect = (messageTemplate) => {
    handleMenuClose();
    if (onMessageTrigger) {
      onMessageTrigger(messageTemplate);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <Paper elevation={compact ? 0 : 2} sx={{ p: compact ? 0 : 3, mb: compact ? 0 : 3, bgcolor: compact ? 'transparent' : colors.bgSecondary }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant={compact ? 'subtitle2' : 'h6'} sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, color: compact ? colors.textPrimary : 'inherit' }}>
          <Search sx={{ fontSize: compact ? 18 : 24, color: '#3B82F6' }} /> Loan Lookup
        </Typography>
        {selectedLoan && !compact && (
          <Chip 
            label="Loan Selected" 
            color="success" 
            icon={<CheckCircle />}
            onDelete={handleClear}
          />
        )}
      </Box>

      {!compact && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Search for a loan to auto-populate message fields with real data
        </Typography>
      )}

      {!compact && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<Info />}>
          Enter an Application Number, Loan ID, or Database ID and click Search to fetch loan details
        </Alert>
      )}

      {/* Compact mode: stacked layout with full-width input */}
      {compact ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ position: 'relative' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Application Number..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              onFocus={() => setShowRecent(true)}
              onBlur={() => setTimeout(() => setShowRecent(false), 200)}
              disabled={loading}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: colors.textSecondary, fontSize: 18 }} />,
                sx: {
                  bgcolor: colors.inputBg,
                  color: colors.textPrimary,
                  '& fieldset': { borderColor: colors.border },
                  '&:hover fieldset': { borderColor: isDark ? '#475569' : '#cbd5e1' },
                  '& input': { color: colors.textPrimary }
                }
              }}
            />
            {recentSearches.length > 0 && showRecent && !searchValue && (
              <Box sx={{ 
                position: 'absolute', 
                top: '100%', 
                left: 0, 
                right: 0, 
                zIndex: 10,
                bgcolor: colors.bgSecondary,
                border: `1px solid ${colors.border}`,
                borderRadius: 1,
                mt: 0.5,
                maxHeight: 150,
                overflow: 'auto'
              }}>
                <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', fontWeight: 600, color: colors.textSecondary }}>
                  Recent Searches
                </Typography>
                <Divider sx={{ borderColor: colors.border }} />
                {recentSearches.slice(0, 3).map((term, idx) => (
                  <Box
                    key={idx}
                    onClick={() => setSearchValue(term)}
                    sx={{
                      px: 2,
                      py: 1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: isDark ? '#334155' : '#f1f5f9' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Refresh sx={{ fontSize: 14, color: colors.textMuted }} />
                    <Typography variant="body2" sx={{ color: colors.textPrimary }}>{term}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSearch}
            disabled={loading || !searchValue.trim()}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}
            sx={{ 
              bgcolor: '#3B82F6',
              color: '#ffffff',
              '&:hover': { bgcolor: '#2563EB' },
              '&.Mui-disabled': { bgcolor: isDark ? '#475569' : '#cbd5e1', color: isDark ? '#94A3B8' : '#64748b' }
            }}
          >
            {loading ? 'Searching...' : 'SEARCH'}
          </Button>
        </Box>
      ) : (
        /* Default mode: side-by-side layout */
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Box sx={{ flexGrow: 1, position: 'relative' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Application Number, Loan ID, or Database ID"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              disabled={loading}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            {recentSearches.length > 0 && !searchValue && (
              <Box sx={{ 
                position: 'absolute', 
                top: '100%', 
                left: 0, 
                right: 0, 
                zIndex: 10,
                bgcolor: 'background.paper',
                boxShadow: 2,
                borderRadius: 1,
                mt: 0.5,
                maxHeight: 200,
                overflow: 'auto'
              }}>
                <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', fontWeight: 600, color: 'text.secondary' }}>
                  Recent Searches
                </Typography>
                <Divider />
                {recentSearches.map((term, idx) => (
                  <Box
                    key={idx}
                    onClick={() => setSearchValue(term)}
                    sx={{
                      px: 2,
                      py: 1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Refresh sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">{term}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading || !searchValue.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <Search />}
            sx={{ minWidth: 120 }}
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </Box>
      )}

      {loanData && !compact && (
        <Card 
          elevation={0} 
          sx={{ 
            border: '2px solid #4caf50',
            bgcolor: '#e8f5e9',
            position: 'relative'
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle sx={{ color: '#4caf50' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                  Loan Details Retrieved
                </Typography>
              </Box>
              <IconButton size="small" onClick={handleClear}>
                <Close />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Application Number
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                  {loanData.essApplicationNumber || loanData._id || 'Loan Details'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Loan Number (ESS)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace', color: loanData.essLoanNumberAlias ? 'inherit' : '#9e9e9e' }}>
                  {loanData.essLoanNumberAlias || 'Not assigned yet'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box>
                  <Chip 
                    label={loanData.status || 'N/A'} 
                    size="small"
                    color={
                      loanData.status === 'ACTIVE' || loanData.status === 'DISBURSED' 
                        ? 'success' 
                        : loanData.status === 'PENDING' 
                        ? 'warning' 
                        : 'default'
                    }
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Client Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {loanData.clientData 
                    ? `${loanData.clientData.firstName || ''} ${loanData.clientData.middleName || ''} ${loanData.clientData.lastName || ''}`.trim().replace(/\s+/g, ' ')
                    : 'N/A'
                  }
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Requested Amount
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatCurrency(loanData.requestedAmount || loanData.amount)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Product Code
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                  {loanData.productCode || loanData.loanData?.productCode || 'N/A'}
                </Typography>
              </Grid>

              {loanData.tenure && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Tenure
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {loanData.tenure} Months
                  </Typography>
                </Grid>
              )}

              {loanData.clientData?.bankAccountNumber && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Bank Account
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                    {loanData.clientData.bankAccountNumber}
                  </Typography>
                </Grid>
              )}

              {loanData.createdAt && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="caption" color="text.secondary">
                    Created Date
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {new Date(loanData.createdAt).toLocaleDateString()}
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Tooltip title="See suggested messages for this loan's status">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSendMessageClick}
                  startIcon={<Send />}
                  sx={{ minWidth: 180, fontWeight: 600 }}
                >
                  💡 Quick Actions
                </Button>
              </Tooltip>
              <Tooltip title={selectedLoan?._id === loanData._id ? "This loan is already loaded" : "Load this loan to enable auto-population on all message cards below"}>
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleUseLoan}
                    disabled={selectedLoan?._id === loanData._id}
                    startIcon={<CheckCircle />}
                    sx={{ minWidth: 160 }}
                  >
                    {selectedLoan?._id === loanData._id ? '✓ Loan Selected' : 'Use This Loan'}
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                onClick={handleClear}
                startIcon={<Refresh />}
              >
                Clear & Search Again
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions Menu - Smart Suggestions Only */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { maxHeight: 500, minWidth: 400 }
        }}
      >
        <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, bgcolor: '#e3f2fd', cursor: 'default' }}>
          <Typography variant="subtitle2" color="primary">💡 Quick Actions - Suggested for This Loan</Typography>
        </MenuItem>
        <Divider />
        {(() => {
          // Get smart suggestions based on loan status
          const status = loanData?.status?.toUpperCase();
          let suggestions = [];
          
          if (status === 'PENDING' || status === 'APPROVED') {
            suggestions = ['LOAN_DISBURSEMENT_NOTIFICATION', 'ACCOUNT_VALIDATION_REQUEST'];
          } else if (status === 'ACTIVE' || status === 'DISBURSED') {
            suggestions = ['LOAN_STATUS_REQUEST', 'LOAN_REPAYMENT_NOTIFICATION'];
          } else if (status === 'DEFAULTED' || status === 'OVERDUE') {
            suggestions = ['DEFAULTER_DETAILS_TO_EMPLOYER', 'LOAN_STATUS_REQUEST'];
          } else {
            // Fallback suggestions for unknown status or no status
            suggestions = ['LOAN_STATUS_REQUEST', 'LOAN_DISBURSEMENT_NOTIFICATION', 'ACCOUNT_VALIDATION_REQUEST'];
          }
          
          // Find message templates for suggestions
          const suggestedMessages = MESSAGE_CATEGORIES
            .flatMap(cat => cat.messages || [])
            .filter(msg => suggestions.includes(msg.type));
          
          return suggestedMessages.map((msg) => (
            <MenuItem 
              key={msg.type} 
              onClick={() => handleMessageSelect(msg)}
              sx={{ 
                pl: 3,
                py: 2,
                '&:hover': {
                  bgcolor: '#e8f5e9',
                },
                borderLeft: '3px solid #4caf50'
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Send fontSize="small" sx={{ color: '#4caf50' }} />
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{msg.displayName}</span>
                    <Chip label="Suggested" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                  </Box>
                }
                secondary={msg.description}
                primaryTypographyProps={{ 
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: '#2e7d32'
                }}
                secondaryTypographyProps={{ 
                  fontSize: '0.75rem',
                  color: '#666'
                }}
              />
            </MenuItem>
          ));
        })()}
        <Divider sx={{ my: 1 }} />
        <MenuItem disabled sx={{ opacity: 0.7, fontSize: '0.75rem', justifyContent: 'center' }}>
          💡 Tip: Browse all 24 messages by category below
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default LoanLookup;
